import { useEffect, useMemo, useState } from "react";
import {
  products,
  more_products,
  ganesh,
  product_benifits,
  GANESH_SEASON_ENABLED,
  DIWALI_SEASON_ENABLED,
} from "../../data/Data";
import { useParams, useNavigate } from "react-router-dom";
import {
  addToCart,
  removeItem,
  updateQuantity,
  getCartTotal,
} from "../../redux/CartSlice";
import { useDispatch } from "react-redux";
import Video from "../../components/Video";
import ProceedToCheckoutView from "../../components/ProceedToCheckoutView";
import Heading from "../../common/Heading";
import { trackViewItem } from "../../analytics/track";
import "./product_item.css";
import ScrollToTop from "../../components/ScrollToTop";
import Seo from "../../components/Seo";

// import { useRef } from "react";
// import { useMouseOverZoom } from "../../hooks/hoverhook";
// import image from "../../image.jpg";
// import "../../css/hover.css";

/**
 * Product descriptions are stored as one long unbroken string, which reads as a
 * wall of text on mobile. This splits them into short paragraphs at sentence
 * boundaries and repairs the missing space after a full stop that appears
 * throughout the data ("...future generations.These idols...").
 */
function toParagraphs(description, sentencesPerParagraph = 2) {
  if (!description) return [];

  const repaired = String(description)
    .replace(/([.!?])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  const sentences = repaired.match(/[^.!?]+[.!?]+|\S[^.!?]*$/g) || [repaired];

  const paragraphs = [];
  for (let i = 0; i < sentences.length; i += sentencesPerParagraph) {
    paragraphs.push(
      sentences
        .slice(i, i + sentencesPerParagraph)
        .join(" ")
        .trim()
    );
  }
  return paragraphs.filter(Boolean);
}

/**
 * Fallback feature badges. Burn time and charcoal-free only mean something for
 * something you light, so clay idols get their own set.
 */
/**
 * Bulk is quoted by weight for incense, which is made and cased by the kilo —
 * but clay idols, kits and hampers are counted, not weighed, so "Orders over
 * 50 kg" was meaningless on a Ganesh page. Same seasonal flags the badges use.
 */
function bulkNoteFor(product) {
  return product?.isGaneshSeasonal || product?.isDiwaliSeasonal
    ? "Wholesale rates by the piece"
    : "Orders over 50 kg";
}

function defaultBadgesFor(product) {
  if (product?.isGaneshSeasonal) {
    return [
      "100% Natural Clay",
      "Biodegradable",
      "Free Chouki Included",
      "Direct from Factory",
    ];
  }
  return ["45+ Mins Burn Time", "Low Smoke", "Charcoal-Free", "Direct from Factory"];
}

export default function ProductItem() {
  // Memoised deliberately: this array is a dependency of the load effect below.
  // Rebuilding it every render gave it a new identity each time, which re-ran the
  // effect on every state change and reset the selected gallery image — so
  // clicking a thumbnail appeared to do nothing.
  const all_products = useMemo(
    () =>
      [...products, ...more_products].filter(
        (item) =>
          (!item.isGaneshSeasonal || GANESH_SEASON_ENABLED) &&
          (!item.isDiwaliSeasonal || DIWALI_SEASON_ENABLED)
      ),
    []
  );

  const [count, setCount] = useState(1);
  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState("single");
  const [selectedProduct, setSelestedProduct] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  let { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [qty, setQty] = useState(1);

  /**
   * Box of 6 is priced flat per product via `boxPrice` in Data.js, not derived
   * from the single price. Only the ₹55 jar range carries one, so the option
   * simply doesn't appear on the Ganesh idols and kits, the Diwali hamper, or
   * the products that are already boxes.
   */
  const purchaseOptions = [
    { id: "single", label: "1 Pack", description: "Perfect for first-time shoppers", packSize: 1 },
    { id: "box", label: "Box of 6", description: "More aroma, better value", packSize: 6 },
    { id: "bulk", label: "Bulk Inquiry", description: bulkNoteFor(selectedProduct), packSize: 0 },
  ];

  const boxAvailable = Boolean(selectedProduct?.boxPrice);

  const availablePurchaseOptions = purchaseOptions.filter(
    (option) => option.id !== "box" || boxAvailable
  );

  const handleQuantityChange = (value) => {
    const next = Math.max(1, value);
    setCount(next);
    setQty(next);
  };

  const incrementCount = () => handleQuantityChange(count + 1);
  const decrementCount = () => handleQuantityChange(count - 1);

  const activeOption =
    availablePurchaseOptions.find((option) => option.id === selectedPurchaseOption) ||
    availablePurchaseOptions[0];

  /** What one unit of the selected option costs — one jar, or one whole box. */
  const unitPriceFor = (product, option) =>
    option?.id === "box"
      ? Number(product.boxPrice)
      : Number(product.price);

  /** MRP for the same unit: a box of six is struck through at six jars' MRP. */
  const unitMainPriceFor = (product, option) =>
    product.mainPrice ? Number(product.mainPrice) * (option?.packSize || 1) : null;

  const handleAddToCart = (item) => {
    if (selectedPurchaseOption === "bulk") {
      return;
    }

    const isMultiPack = activeOption.id !== "single";
    const unitPrice = unitPriceFor(item, activeOption);

    /**
     * The multiplier has to ride on `price`, not just `totalPrice`.
     * CartSlice recomputes `totalPrice = price * quantity` whenever the
     * quantity changes, so a box whose markup lived only in totalPrice would
     * silently revert to single-jar pricing the first time someone tapped + or
     * − in the cart.
     *
     * A multi-pack also gets its own id, so it sits as its own cart line
     * instead of merging into an existing single-jar line (addToCart matches on
     * id and keeps the existing line's price).
     */
    const tempProduct = {
      ...item,
      id: isMultiPack ? `${item.id}-${activeOption.id}` : item.id,
      product_name: isMultiPack
        ? `${item.product_name} (${activeOption.label})`
        : item.product_name,
      product_name_key: isMultiPack
        ? `${item.product_name_key}_${activeOption.id}`
        : item.product_name_key,
      price: unitPrice,
      mainPrice: unitMainPriceFor(item, activeOption),
      quantity: qty,
      totalPrice: qty * unitPrice,
      purchaseOption: activeOption.id,
    };
    dispatch(addToCart(tempProduct));
    dispatch(getCartTotal());
  };

  function setImage(url) {
    setMainImage(url);
  }

  // Deliberately keyed on selectedProduct rather than the route id: the product
  // is resolved asynchronously below, so firing on id alone would report a
  // ViewContent with no name and no value, which Meta cannot use for a catalogue
  // match or a value-optimised audience.
  useEffect(() => {
    if (selectedProduct) trackViewItem(selectedProduct);
  }, [selectedProduct]);

  useEffect(() => {
    // Not every product offers a Box of 6, so the choice can't carry across
    // products — otherwise a jar's box selection would follow you onto a Ganesh
    // idol that has no box at all.
    setSelectedPurchaseOption("single");
    const loadProduct = () => {
      if (id === "Eco_Friendly_Ganesh_Kit") {
        if (!GANESH_SEASON_ENABLED) {
          navigate("/shop");
          return;
        }
        setSelestedProduct(ganesh);
        setMainImage(ganesh.gallery?.[0]?.url_s || ganesh.product_img);
        return;
      }

      if (id === "Diwali_Hamper") {
        if (!DIWALI_SEASON_ENABLED) {
          navigate("/shop");
          return;
        }
      }

      const matchingProduct = all_products.find(
        (item) => id === item.product_name_key
      );

      if (matchingProduct) {
        setSelestedProduct(matchingProduct);
        setMainImage(matchingProduct.gallery?.[0]?.url_s || matchingProduct.product_img);
      } else {
        navigate("/shop");
      }
    };

    loadProduct();
  }, [id, all_products, navigate]);

  /**
   * Cross-sell. A single ₹55 jar barely covers the parcel, so basket size is
   * the whole economics of shipping here. Same category first, since that is
   * the closest thing to "goes with this" the data supports; topped up from the
   * rest of the catalogue if the category is thin. Out-of-stock items are left
   * out — no point routing someone to a dead end.
   */
  const relatedProducts = useMemo(() => {
    if (!selectedProduct) return [];
    const pool = all_products.filter(
      (item) =>
        item.product_name_key !== selectedProduct.product_name_key &&
        !item.out_of_stock
    );
    const sameCategory = pool.filter(
      (item) => item.category && item.category === selectedProduct.category
    );
    const rest = pool.filter((item) => !sameCategory.includes(item));
    return [...sameCategory, ...rest].slice(0, 4);
  }, [all_products, selectedProduct]);

  // Collapse repeated photos so the thumbnail strip only appears when it adds something.
  const uniqueGallery = (() => {
    const gallery = selectedProduct?.gallery || [];
    const seen = new Set();
    return gallery.filter((item) => {
      if (!item?.url_s || seen.has(item.url_s)) return false;
      seen.add(item.url_s);
      return true;
    });
  })();

  const productSchema = selectedProduct
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: selectedProduct.product_name_detail || selectedProduct.product_name,
        image: [selectedProduct.product_img],
        description: selectedProduct.description,
        sku: selectedProduct.product_name_key || selectedProduct.id,
        brand: {
          "@type": "Brand",
          name: "NT Sticks",
        },
        offers: {
          "@type": "Offer",
          priceCurrency: "INR",
          price: selectedProduct.price,
          availability: selectedProduct.out_of_stock
            ? "https://schema.org/OutOfStock"
            : "https://schema.org/InStock",
          url: window.location.href,
        },
        // NOTE: no aggregateRating here on purpose. Google's structured-data
        // policy requires review markup to reflect genuine reviews collected
        // from customers; emitting invented ratings risks a "spammy structured
        // markup" manual action. Add this back only when real reviews exist.
      }
    : null;

  return (
    <div>
      {selectedProduct && (
        <Seo
          title={selectedProduct.meta_title}
          description={selectedProduct.meta_description}
          /* Strips the leading "../" so shares resolve against the site root. */
          image={String(selectedProduct.product_img || "").replace(/^\.\.\//, "/")}
        >
          {productSchema && (
            <script type="application/ld+json">
              {JSON.stringify(productSchema)}
            </script>
          )}
        </Seo>
      )}
      <Heading title="Home" subtitle="Products" />
      {selectedProduct && (
        <div className="professional-product-container">
          <div className="container py-5">
            <div className="row">
              {/* Product Images Section */}
              <div className="col-lg-6 col-md-12">
                <div className="product-images-section">
                  {/* Main Product Image */}
                  <div className="main-image-container">
                    <img
                      className="main-product-image"
                      src={mainImage ? mainImage : selectedProduct.product_img}
                      alt={selectedProduct.product_name}
                    />
                    <div className="image-overlay">
                      <i className="fas fa-search-plus zoom-icon"></i>
                    </div>
                  </div>
                  
                  {/* Image Gallery Thumbnails.
                      Was restricted to the Ganesh range; now shown for any product
                      that has more than one DISTINCT photo. 22 of 34 galleries in
                      Data.js are the same image repeated, and a strip of identical
                      thumbnails reads as broken — so those render nothing until
                      real alternate shots are added. */}
                  {uniqueGallery.length > 1 && (
                    <div className="image-gallery">
                      {uniqueGallery.map((item, index) => (
                        <div 
                          key={index}
                          className={`gallery-thumbnail ${
                            (mainImage || selectedProduct.product_img) === item.url_s ? 'active' : ''
                          }`}
                          onClick={() => setImage(item.url_s)}
                        >
                          <img
                            src={item.url_s}
                            alt={`${selectedProduct.product_name} ${index + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Details Section */}
              <div className="col-lg-6 col-md-12">
                <div className="product-details-section">
                  {/* Product Title */}
                  <h1 className="product-title">
                    {selectedProduct.product_name_detail
                      ? selectedProduct.product_name_detail
                      : selectedProduct.product_name}
                  </h1>
                  
                  {/* Product Price.
                      Tracks the selected pack — it used to show the single-jar
                      price no matter which option was active, so a Box of 6
                      read as ₹55. Bulk has no price of its own (it routes to a
                      quote), so it falls back to the single-jar figure. */}
                  <div className="price-section">
                    <span className="current-price">
                      ₹{selectedPurchaseOption === "bulk"
                        ? selectedProduct.price
                        : unitPriceFor(selectedProduct, activeOption)}
                      .00
                    </span>
                    {selectedProduct.mainPrice && (
                      <span className="original-price">
                        ₹{selectedPurchaseOption === "bulk"
                          ? selectedProduct.mainPrice
                          : unitMainPriceFor(selectedProduct, activeOption)}
                      </span>
                    )}
                    {activeOption?.id === "box" && (
                      <span className="price-pack-note">for one {activeOption.label}</span>
                    )}
                  </div>

                  {/* Stock Status */}
                  <div className="stock-status">
                    {!selectedProduct.out_of_stock ? (
                      <span className="in-stock">
                        <i className="fas fa-check-circle mr-2"></i>
                        In Stock
                      </span>
                    ) : (
                      <span className="out-of-stock">
                        <i className="fas fa-times-circle mr-2"></i>
                        Out of Stock
                      </span>
                    )}
                  </div>

                  {/* Product Benefits */}
                  {/* <div className="product-benefits">
                    <h6 className="benefits-title">
                      <i className="fas fa-leaf mr-2"></i>
                      Product Benefits
                    </h6> */}
                    {/* <div className="benefits-grid">
                      {product_benifits.map((item, index) => (
                        <div key={index} className="benefit-item">
                          <div className="benefit-icon">
                            <img src={item.image} alt={item.name} />
                          </div>
                          <span className="benefit-name">{item.name}</span>
                        </div>
                      ))}
                    </div> */}
                  {/* </div> */}
                  <div className="purchase-section">
                    <div className="purchase-options">
                      {availablePurchaseOptions.map((option) => (
                        <button
                          key={option.id}
                          className={`purchase-option-btn ${selectedPurchaseOption === option.id ? 'active' : ''}`}
                          onClick={() => setSelectedPurchaseOption(option.id)}
                          type="button"
                        >
                          <strong>{option.label}</strong>
                          <span>{option.description}</span>
                        </button>
                      ))}
                    </div>

                    {selectedPurchaseOption !== 'bulk' ? (
                      <>
                        <div className="quantity-selector">
                          <label className="quantity-label">Quantity:</label>
                          <div className="quantity-controls">
                            <button
                              className="quantity-btn minus"
                              onClick={decrementCount}
                              disabled={count <= 1}
                            >
                              <i className="fas fa-minus"></i>
                            </button>
                            <input
                              type="number"
                              className="quantity-input"
                              value={count || 1}
                              readOnly
                            />
                            <button
                              className="quantity-btn plus"
                              onClick={incrementCount}
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          </div>
                        </div>
                        <button
                          disabled={selectedProduct.out_of_stock}
                          onClick={() => handleAddToCart(selectedProduct)}
                          className="add-to-cart-btn"
                        >
                          <i className="fas fa-shopping-cart mr-2"></i>
                          {selectedProduct.out_of_stock ? 'Out of Stock' : 'Add to Cart'}
                        </button>
                      </>
                    ) : (
                      <button
                        className="request-bulk-btn"
                        onClick={() => navigate('/wholesale')}
                      >
                        <i className="fas fa-handshake mr-2"></i>
                        Request Bulk Quote
                      </button>
                    )}
                  </div>

                  {/* Defaults are chosen per product type. Previously every
                      product fell back to incense badges, so clay idols
                      advertised "45+ Mins Burn Time" and "Charcoal-Free". */}
                  <div className="product-badges">
                    {(selectedProduct.badges || defaultBadgesFor(selectedProduct)).map((badge, index) => (
                      <span key={index} className="badge-pill">
                        <i className="fas fa-check-circle"></i> {badge}
                      </span>
                    ))}
                  </div>

                  <div className="product-description">
                    <div className="description-header">
                      <h6 className="description-title">
                        <i className="fas fa-info-circle mr-2"></i>
                        Product Description
                      </h6>
                    </div>
                    <div className="description-content">
                      {(() => {
                        const paragraphs = toParagraphs(selectedProduct.description);
                        const isLong = paragraphs.length > 2;
                        const visible =
                          isLong && !descriptionExpanded
                            ? paragraphs.slice(0, 2)
                            : paragraphs;

                        return (
                          <>
                            {visible.map((para, index) => (
                              <p key={index}>{para}</p>
                            ))}
                            {isLong && (
                              <button
                                type="button"
                                className="description-toggle"
                                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                                aria-expanded={descriptionExpanded}
                              >
                                {descriptionExpanded ? "Show less" : "Read full description"}
                                <i
                                  className={`fas fa-chevron-${
                                    descriptionExpanded ? "up" : "down"
                                  } ml-2`}
                                ></i>
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Only shown for products with real, product-specific notes.
                      This used to fall back to a hardcoded Lavender/Sandalwood/Musk
                      pyramid, which meant 33 of 34 products advertised a scent
                      profile that wasn't theirs — including the clay idols. */}
                  {selectedProduct.fragranceNotes && (
                    <div className="fragrance-anatomy">
                      <h6 className="anatomy-title">Fragrance Anatomy</h6>
                      <div className="anatomy-grid">
                        {[
                          { label: 'Top Notes', values: selectedProduct.fragranceNotes.top },
                          { label: 'Middle Notes', values: selectedProduct.fragranceNotes.middle },
                          { label: 'Base Notes', values: selectedProduct.fragranceNotes.base },
                        ]
                          .filter((note) => note.values && note.values.length)
                          .map((note, index) => (
                            <div key={index} className="anatomy-card">
                              <strong>{note.label}</strong>
                              <p>{note.values.join(' • ')}</p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Features */}
                  <div className="additional-features">
                    <div className="feature-item">
                      <i className="fas fa-truck mr-2"></i>
                      <span>Free Delivery on orders above ₹200</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-undo mr-2"></i>
                      <span>Damage covered — report within 48 hours</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-shield-alt mr-2"></i>
                      <span>100% Authentic Products</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedProduct && relatedProducts.length > 0 && (
        <section className="related-products">
          <div className="container">
            <div className="related-head">
              <h2>Pairs well with</h2>
              <p>More from our Indore factory, same natural blends.</p>
            </div>
            <div className="related-grid">
              {relatedProducts.map((item) => (
                <div
                  key={item.product_name_key}
                  className="related-card"
                  onClick={() => navigate(`/item/${item.product_name_key}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/item/${item.product_name_key}`);
                    }
                  }}
                >
                  <div className="related-thumb">
                    <img src={item.product_img} alt={item.product_name} loading="lazy" />
                  </div>
                  <div className="related-body">
                    <h3>{item.product_name}</h3>
                    <div className="related-price">
                      <strong>₹{item.price}</strong>
                      {item.mainPrice && Number(item.mainPrice) > Number(item.price) && (
                        <s>₹{item.mainPrice}</s>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ProceedToCheckoutView />
      <ScrollToTop />
    </div>
  );
}
